import React, { useMemo } from 'react';
import {
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { PieChart } from 'react-native-gifted-charts';
import SkeletonPlaceholder from 'react-native-skeleton-placeholder';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import {
  GTFonts,
  useLayoutSize,
  useTheme,
  withOpacity,
  type Theme,
} from '../../../theme';
import { fs, formatMoney, iconSize, s, v } from '../../../utils/libs';
import { formatSharePercent } from '../../../utils/paymentBreakdown';
import {
  getExpenseCategoryColor,
  getExpenseCategoryIcon,
  type ExpenseCategoryShare,
} from '../../../utils/expenseCategories';

type Props = {
  rows: ExpenseCategoryShare[];
  currencyText: string;
  /** First load: a placeholder ring and rows. */
  loading: boolean;
  /** The summary failed to load. */
  failed: boolean;
  /** The lit category (index into `rows`); the others dim. */
  selectedIndex: number | null;
  onSelect: (index: number) => void;
  style?: StyleProp<ViewStyle>;
};

const DONUT_RADIUS = v(68);
const DONUT_INNER_RADIUS = v(46);
const DONUT_SIZE = DONUT_RADIUS * 2;
const DIMMED_OPACITY = 0.35;

/**
 * Where the period's money went: a donut over the categories ranked by
 * amount. Each category keeps the colour and icon it has in Latest Expenses.
 * Tapping a row or a slice lights it; tapping it again clears the light.
 */
const ExpenseCategoryCard: React.FC<Props> = ({
  rows,
  currencyText,
  loading,
  failed,
  selectedIndex,
  onSelect,
  style,
}) => {
  const { theme } = useTheme();
  const { useSidebarNav } = useLayoutSize();
  const styles = useMemo(
    () => createStyles(theme, useSidebarNav),
    [theme, useSidebarNav],
  );

  const total = rows.reduce((sum, row) => sum + row.amount, 0);
  const colors = rows.map(row =>
    getExpenseCategoryColor(row.type, theme.colors.textSecondary),
  );
  const pieData = rows.map((row, index) => ({
    value: row.amount,
    color:
      selectedIndex !== null && selectedIndex !== index
        ? withOpacity(colors[index], DIMMED_OPACITY)
        : colors[index],
    focused: selectedIndex === index,
  }));

  const renderMessage = (
    icon: 'alert-circle-outline' | 'receipt-outline',
    text: string,
  ) => (
    <View style={styles.messageWrap}>
      <MaterialCommunityIcons
        name={icon}
        size={fs(24)}
        color={theme.colors.textTertiary}
      />
      <Text style={styles.messageText}>{text}</Text>
    </View>
  );

  const renderBody = () => {
    if (failed) {
      return renderMessage(
        'alert-circle-outline',
        'Expenses could not be loaded right now',
      );
    }
    if (loading) {
      return (
        <View style={styles.content}>
          <View style={styles.donutWrap}>
            <View style={styles.ringPlaceholder} />
          </View>
          <View style={styles.rows}>
            {[0, 1].map(i => (
              <SkeletonPlaceholder
                key={i}
                backgroundColor={theme.colors.border}
                highlightColor={theme.colors.surfaceSecondary}>
                <SkeletonPlaceholder.Item
                  flexDirection="row"
                  alignItems="center"
                  paddingVertical={v(8)}>
                  <SkeletonPlaceholder.Item
                    width={s(36)}
                    height={s(36)}
                    borderRadius={s(10)}
                  />
                  <SkeletonPlaceholder.Item
                    marginLeft={s(12)}
                    width={s(i === 0 ? 84 : 110)}
                    height={v(14)}
                    borderRadius={s(4)}
                  />
                </SkeletonPlaceholder.Item>
              </SkeletonPlaceholder>
            ))}
          </View>
        </View>
      );
    }
    if (rows.length === 0 || total <= 0) {
      return renderMessage('receipt-outline', 'No expenses in this period');
    }
    return (
      <View style={styles.content}>
        <View style={styles.donutWrap}>
          <PieChart
            data={pieData}
            donut
            isAnimated
            radius={DONUT_RADIUS}
            innerRadius={DONUT_INNER_RADIUS}
            extraRadius={v(6)}
            strokeWidth={2}
            strokeColor={theme.colors.surface}
            innerCircleColor={theme.colors.surface}
            backgroundColor={theme.colors.surface}
            onPress={(_item: unknown, index: number) => onSelect(index)}
            centerLabelComponent={() => (
              <View style={styles.donutCenter}>
                <Text
                  style={styles.donutCenterValue}
                  numberOfLines={1}
                  adjustsFontSizeToFit>
                  {currencyText} {formatMoney(total)}
                </Text>
                <Text style={styles.donutCenterLabel}>Total</Text>
              </View>
            )}
          />
        </View>
        <View style={styles.rows}>
          {rows.map((row, index) => {
            const selected = selectedIndex === index;
            const dimmed = selectedIndex !== null && !selected;
            const amountText = `${currencyText} ${formatMoney(row.amount)}`;
            const shareText = formatSharePercent(row.share);
            return (
              <React.Fragment key={row.type}>
                {index > 0 ? <View style={styles.rowDivider} /> : null}
                <Pressable
                  onPress={() => onSelect(index)}
                  accessibilityRole="button"
                  accessibilityState={{ selected }}
                  accessibilityLabel={`${row.label}, ${amountText}, ${shareText}`}
                  style={({ pressed }) => [
                    styles.row,
                    dimmed && styles.rowDimmed,
                    pressed && styles.rowPressed,
                  ]}>
                  <View
                    style={[
                      styles.iconTile,
                      { backgroundColor: colors[index] + '14' },
                    ]}>
                    <MaterialCommunityIcons
                      name={getExpenseCategoryIcon(row.type)}
                      size={iconSize(18)}
                      color={colors[index]}
                    />
                  </View>
                  <Text style={styles.rowName}>{row.label}</Text>
                  <View style={styles.rowValues}>
                    <Text style={styles.rowAmount}>{amountText}</Text>
                    <Text style={styles.rowShare}>{shareText}</Text>
                  </View>
                </Pressable>
              </React.Fragment>
            );
          })}
        </View>
      </View>
    );
  };

  return (
    <View style={[styles.card, style]}>
      <Text style={styles.title}>Expenses by Category</Text>
      {renderBody()}
    </View>
  );
};

export default ExpenseCategoryCard;

const createStyles = (theme: Theme, useSidebarNav: boolean) =>
  StyleSheet.create({
    card: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      paddingHorizontal: s(16),
      paddingTop: v(14),
      paddingBottom: v(6),
      ...theme.shadows.sm,
    },
    title: {
      color: theme.colors.text,
      fontFamily: GTFonts.bold,
      fontSize: fs(16),
      lineHeight: fs(20),
    },
    // Phones stack the donut over the rows; the sidebar layout puts them side
    // by side and wraps back to stacked when the card is narrow.
    content: useSidebarNav
      ? {
          flex: 1,
          flexDirection: 'row',
          flexWrap: 'wrap',
          alignItems: 'center',
          alignContent: 'center',
          columnGap: s(20),
          rowGap: v(12),
          marginTop: v(12),
          paddingBottom: v(8),
        }
      : {
          marginTop: v(12),
        },
    donutWrap: {
      alignItems: 'center',
      justifyContent: 'center',
      ...(useSidebarNav ? { marginHorizontal: 'auto' } : null),
    } as ViewStyle,
    ringPlaceholder: {
      width: DONUT_SIZE,
      height: DONUT_SIZE,
      borderRadius: DONUT_RADIUS,
      borderWidth: DONUT_RADIUS - DONUT_INNER_RADIUS,
      borderColor: theme.colors.border,
    },
    donutCenter: {
      // Bounds the total so a long amount shrinks inside the ring.
      width: DONUT_INNER_RADIUS * 2 - s(8),
      alignItems: 'center',
      justifyContent: 'center',
    },
    donutCenterValue: {
      color: theme.colors.text,
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
      textAlign: 'center',
    },
    donutCenterLabel: {
      color: theme.colors.textSecondary,
      fontFamily: GTFonts.regular,
      fontSize: fs(11),
      lineHeight: fs(14),
      textAlign: 'center',
    },
    rows: useSidebarNav
      ? { flexGrow: 1, flexShrink: 1, flexBasis: s(220), minWidth: 0 }
      : { marginTop: v(8) },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      columnGap: s(12),
      paddingVertical: v(8),
      ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    } as ViewStyle,
    rowDimmed: {
      opacity: DIMMED_OPACITY,
    },
    rowPressed: {
      opacity: 0.7,
    },
    rowDivider: {
      height: 1,
      marginLeft: s(48),
      backgroundColor: theme.colors.border,
    },
    // Same tile as the Latest Expenses rows
    iconTile: {
      width: s(36),
      height: s(36),
      borderRadius: s(10),
      alignItems: 'center',
      justifyContent: 'center',
    },
    rowName: {
      flex: 1,
      minWidth: s(64),
      color: theme.colors.text,
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
    },
    // Phones: amount and share side by side. Beside the donut (sidebar) the
    // row is narrower, so the share goes under the amount and names never break.
    rowValues: useSidebarNav
      ? { flexShrink: 0, alignItems: 'flex-end' }
      : {
          flexShrink: 1,
          flexDirection: 'row',
          alignItems: 'center',
          columnGap: s(12),
        },
    rowAmount: {
      flexShrink: useSidebarNav ? 0 : 1,
      textAlign: 'right',
      color: theme.colors.text,
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
    },
    rowShare: {
      ...(useSidebarNav ? null : { width: s(56) }),
      textAlign: 'right',
      color: theme.colors.textSecondary,
      fontFamily: GTFonts.semibold,
      fontSize: fs(12),
      lineHeight: fs(18),
    },
    messageWrap: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingTop: v(22),
      paddingBottom: v(30),
      rowGap: v(6),
    },
    messageText: {
      color: theme.colors.textSecondary,
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      textAlign: 'center',
    },
  });
